import { useEffect, useMemo, useRef, useState } from "react";
import { Building, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { useSaveBarSection } from "@/components/portal/SaveBar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn, dirtyRing } from "@/lib/utils";

type ApSettingsResponse = components["schemas"]["ApSettingsResponse"];
type PatchApSettingsRequest = components["schemas"]["PatchApSettingsRequest"];
type ApPaymentLinkMode = components["schemas"]["ApPaymentLinkMode"];

// Matching knobs are hidden in the portal: the shipped defaults are what we want everywhere. Values still load and save.
const SHOW_MATCHING_SETTINGS = false;

// Numeric knobs only: the two auto-match route switches are Switches and must not land here.
type NumericFieldKey = Exclude<
  keyof PatchApSettingsRequest,
  | "apEmailsEnabled"
  | "payByBankEnabled"
  | "billReviewTimeAfterDueDate"
  | "gmailConnectorEnabled"
  | "gmailAllowedRedirectHosts"
  | "paymentAllowedRedirectHosts"
  | "paymentLinkMode"
  | "defaultBillPaymentLinkUrl"
  | "poAutoMatchEnabled"
  | "dnAutoMatchEnabled"
  // Retired server-side (kept on the type so stale callers get an error); never sent, not a knob.
  | "matchAutoAcceptEnabled"
  | "duplicateBillDetectionEnabled"
  | "companyAutoAssignEnabled"
>;

interface FieldSpec {
  key: NumericFieldKey;
  label: string;
  help: string;
  min: number;
  max?: number;
  step: number;
  integer?: boolean;
  suffix?: string;
}

const AUTO_ACCEPT_FIELDS: FieldSpec[] = [
  {
    key: "matchAutoAcceptThreshold",
    label: "Auto-accept score",
    help: "Match score (0–1) at or above which a counterpart is matched automatically. This is the only bar — a bill line is routinely settled by several orders and several deliveries, so EVERY candidate reaching it is matched, up to the quantity billed on the line. The score combines description similarity with price, quantity and date agreement. Must be at or above the line match similarity threshold.",
    min: 0,
    max: 1,
    step: 0.05,
  },
];

// Delivery-address routing bars, independent of the line-matching knobs; the backend imposes no floor between them.
const COMPANY_ROUTING_FIELDS: FieldSpec[] = [
  {
    key: "companyAutoAssignThreshold",
    label: "Routing score",
    help: "How closely the delivery address on a bill must match an organisation's address before the bill is assigned to it automatically. An exact postcode match scores 1. Below this bar the bill waits in the company inbox. Raise it if bills reach the wrong site; lower it if too many sit waiting.",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "companyAutoAssignMargin",
    // Named for the subsystem ("Routing") so it isn't mistaken for the removed matching ambiguity margin.
    label: "Routing ambiguity margin",
    help: "How far ahead the best-matching organisation must be before the bill is assigned automatically. Two sites in the same postcode district both score highly, and picking between them on a hair's breadth is how a bill lands on the wrong one - so a close call always waits for a person.",
    min: 0,
    max: 1,
    step: 0.05,
  },
];

// Per-variance tolerances (eng-docs §1a.9). Each both gates settling an auto-matched line and sets the dead-zone
// where that variance doesn't lower confidence.
const VARIANCE_FIELDS: FieldSpec[] = [
  {
    key: "matchMaxPriceVarianceFraction",
    label: "Price tolerance",
    help: "How far a bill's unit price may differ from the purchase order's, as a fraction of the ordered price (0.05 = 5%). Within this band the difference is treated as clean: it doesn't lower the match confidence, and on an automatically matched line it is settled with no reviewer. Beyond it, the difference lowers confidence and the line is left for someone to accept. Lower it to scrutinise price more tightly; raise it to tolerate more.",
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: "matchMaxReceiptQuantityVarianceFraction",
    label: "Delivery quantity tolerance",
    help: "How far the quantity billed may differ from the quantity a delivery note records as received, as a fraction of the billed quantity (0.05 = 5%). A short delivery is always treated as the right note and never lowers confidence — but a shortfall beyond this band is held for a reviewer rather than settled automatically. An over-receipt (more received than billed) within the band is clean; beyond it, it also lowers confidence. Governs both the auto-settle decision and the confidence dead-zone.",
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: "matchMaxOrderQuantityVarianceFraction",
    label: "Order quantity tolerance",
    help: "How far the quantity billed may exceed the quantity ordered on a purchase order, as a fraction of the ordered quantity (0.05 = 5%). Billing at or below the order is always clean. An over-order within this band doesn't lower confidence and, on an automatically matched line, is settled with no reviewer; beyond it, the over-order lowers confidence and the line is held for a reviewer.",
    min: 0,
    max: 1,
    step: 0.01,
  },
];

const LINE_FIELDS: FieldSpec[] = [
  {
    key: "matchSimilarityThreshold",
    label: "Line match similarity threshold",
    help: "Minimum description similarity (0–1) for a PO or delivery-note line to be suggested at all. Exact SKU matches bypass this. Below this bar a candidate isn't a weak suggestion — it isn't offered at all, so lower it if genuine matches are missing because the supplier's wording differs from the bill's. Real invoices diverge more than you'd expect: \"4307 Wrap Core Reinf 400mm, Pallet\" against a delivery note's \"Pallet Wrap Reinforced\" — the same product — scores about 0.52. Default 0.6.",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "matchRoundingTolerance",
    label: "Rounding tolerance",
    help: "Per-line amount difference treated as reconciled, in the bill's currency (e.g. 0.01 = 1p).",
    min: 0,
    step: 0.01,
  },
  {
    key: "poMatchWindowDaysBefore",
    label: "Order window — days before",
    help: "How many days a purchase order's expected delivery date may fall BEFORE the invoice date and still score full marks. Past it the date score tapers evenly to nothing at 30 days from the invoice, so a stale order ranks below a timely one without ever being hidden.",
    min: 0,
    max: 30,
    step: 1,
    integer: true,
    suffix: "days",
  },
  {
    key: "poMatchWindowDaysAfter",
    label: "Order window — days after",
    help: "The same, for an expected delivery date landing AFTER the invoice date. Separate from the before window because an order's expected delivery legitimately falls either side of the invoice, unlike a delivery note's received date.",
    min: 0,
    max: 30,
    step: 1,
    integer: true,
    suffix: "days",
  },
  {
    key: "dnMatchWindowDaysBefore",
    label: "Delivery window — days before",
    help: "How many days a delivery note's received date may fall BEFORE the invoice date and still score full marks. Usually the largest of the four: goods are received before they are invoiced, and one bill often covers a period of separate drops. Past it the score tapers to nothing at 30 days.",
    min: 0,
    max: 30,
    step: 1,
    integer: true,
    suffix: "days",
  },
  {
    key: "dnMatchWindowDaysAfter",
    label: "Delivery window — days after",
    help: "How many days a delivery note's received date may fall AFTER the invoice date and still score full marks. Usually the smallest: an invoice raised just before the period's last delivery is normal, a note from the following month is not.",
    min: 0,
    max: 30,
    step: 1,
    integer: true,
    suffix: "days",
  },
];

const SUPPLIER_FIELDS: FieldSpec[] = [
  {
    key: "supplierMatchSimilarityThreshold",
    label: "Supplier match threshold",
    help: "Minimum combined supplier score (0–1) to suggest a fuzzy supplier link. Exact tax-id / email / name matches bypass this and auto-link.",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "supplierMatchNameWeight",
    label: "Name weight",
    help: "Relative weight of supplier-name similarity in the combined score. Normalised against the address weight, so the two need not sum to 1.",
    min: 0,
    step: 0.1,
  },
  {
    key: "supplierMatchAddressWeight",
    label: "Address weight",
    help: "Relative weight of delivery-address similarity in the combined score. Normalised against the name weight.",
    min: 0,
    step: 0.1,
  },
];

const LINK_AFFINITY_FIELDS: FieldSpec[] = [
  {
    key: "manualLinkSameItemBoost",
    label: "Same item boost",
    help: "How much to raise the score of lines whose description repeats one you've already confirmed for this bill line — on any purchase order or delivery note. This is what carries a split delivery: 12 units billed once but delivered as 2, 3, 4 and 3 across four notes that each list the identical line. Confirm the first and the other three rise to the top. 0.1 scores them 10% higher; 0 switches it off.",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "manualLinkSameDocumentBoost",
    label: "Same document boost",
    help: "How much to raise the score of lines on a purchase order or delivery note you've already linked somewhere on this bill — the rest of a bill usually comes off the same order or delivery. Applies to the bill's other lines, where the same item boost applies to the line you confirmed. 0.1 scores them 10% higher; 0 switches it off.",
    min: 0,
    max: 1,
    step: 0.05,
  },
];

const ALL_FIELDS = [
  ...LINE_FIELDS,
  ...AUTO_ACCEPT_FIELDS,
  ...LINK_AFFINITY_FIELDS,
  ...COMPANY_ROUTING_FIELDS,
  ...VARIANCE_FIELDS,
  ...SUPPLIER_FIELDS,
];

/** Knob values are held as strings so inputs can be cleared mid-edit; parsed on save. */
type FormState = Record<NumericFieldKey, string>;

interface OperationalState {
  apEmailsEnabled: boolean;
  billForwardingSlug: string;
  billReviewTtlDays: string;
  gmailConnectorEnabled: boolean;
  gmailAllowedRedirectHosts: string;
  payByBankEnabled: boolean;
  paymentAllowedRedirectHosts: string;
  paymentLinkMode: ApPaymentLinkMode;
  defaultBillPaymentLinkUrl: string;
  // Matching toggles rather than operational config, but they're booleans and ride the same
  // dirty-tracking/Switch machinery as the others.
  poAutoMatchEnabled: boolean;
  dnAutoMatchEnabled: boolean;
  duplicateBillDetectionEnabled: boolean;
  companyAutoAssignEnabled: boolean;
}

/** Single DNS label: 1–63 chars, lowercase letters/digits/hyphens, no leading or trailing hyphen. */
function isValidSlug(value: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(value);
}

function parseHosts(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((h) => h.trim())
    .filter((h) => h.length > 0);
}

// The payment link approved bills carry (eng-docs "AP - Bill Payment Links"). Shown on the bill, in bill
// webhooks and in the ready-to-pay email to the Organisation's admins.
const PAYMENT_LINK_OPTIONS: { value: ApPaymentLinkMode; label: string; description: string }[] = [
  {
    value: "None",
    label: "No payment link",
    description: "Bills carry no link. Organisations pay outside Dolfin and mark bills as paid.",
  },
  {
    value: "Dolfin",
    label: "Dolfin pay-by-bank page",
    description: "A page branded for your client where the Organisation pays straight from its bank account.",
  },
  {
    value: "BringYourOwn",
    label: "Your own payment link",
    description: "Send Organisations to your own payment page, with the bill's details filled in.",
  },
];

// Obviously-sample ids for the live example under the template, so staff can see what a bill's link becomes.
const SAMPLE_TOKEN_VALUES: Record<string, string> = {
  "{billId}": "3f2b9c1e-7a4d-4e8b-9c2f-1d6e5a7b8c90",
  "{organisationId}": "8c1d4e2f-5b6a-4c7d-8e9f-0a1b2c3d4e5f",
  "{supplierId}": "a7e3f9b1-2c4d-4f6a-8b0c-9d1e2f3a4b5c",
};

/** Expands a template the way the backend does (each token URL-encoded); unknown tokens are left as typed. */
function expandSampleLink(template: string): string {
  return template.replace(/\{[^}]*\}/g, (token) =>
    token in SAMPLE_TOKEN_VALUES ? encodeURIComponent(SAMPLE_TOKEN_VALUES[token]!) : token,
  );
}

/** Whether a failed PATCH is about payment links (ApSettings.Invalid is shared, so told apart by wording). */
function isPaymentLinkProblem(problem: { code?: string; detail?: string } | undefined, body: PatchApSettingsRequest) {
  if (problem?.code === "ApSettings.PaymentLinkModeNotAllowed") return true;
  // shortcut: matches the backend's ValidatePaymentLink messages by prefix; switch to a field key if the problem ever carries one.
  return (
    problem?.code === "ApSettings.Invalid" &&
    body.defaultBillPaymentLinkUrl !== undefined &&
    /^(Payment link|Unknown template token)/.test(problem.detail ?? "")
  );
}

function toFormState(settings: ApSettingsResponse): FormState {
  const state = {} as FormState;
  for (const field of ALL_FIELDS) {
    const value = settings[field.key as keyof ApSettingsResponse];
    state[field.key] = value == null ? "" : String(value);
  }
  return state;
}

function toOperationalState(settings: ApSettingsResponse): OperationalState {
  return {
    // Opt-OUT kill switch, so an absent value means on — mirroring ApSettingsDefaults on the backend.
    apEmailsEnabled: settings.apEmailsEnabled ?? true,
    billForwardingSlug: settings.billForwardingSlug ?? "",
    billReviewTtlDays: String(settings.billReviewTimeAfterDueDate ?? ""),
    gmailConnectorEnabled: settings.gmailConnectorEnabled ?? false,
    gmailAllowedRedirectHosts: (settings.gmailAllowedRedirectHosts ?? []).join("\n"),
    payByBankEnabled: settings.payByBankEnabled ?? false,
    paymentAllowedRedirectHosts: (settings.paymentAllowedRedirectHosts ?? []).join("\n"),
    paymentLinkMode: settings.paymentLinkMode ?? "None",
    defaultBillPaymentLinkUrl: settings.defaultBillPaymentLinkUrl ?? "",
    // Both default ON, mirroring ApSettingsDefaults.
    poAutoMatchEnabled: settings.poAutoMatchEnabled ?? true,
    dnAutoMatchEnabled: settings.dnAutoMatchEnabled ?? true,
    // Opt-OUT, so an absent value means on — mirroring ApSettingsDefaults on the backend.
    duplicateBillDetectionEnabled: settings.duplicateBillDetectionEnabled ?? true,
    // Opt-OUT, so an absent value means on — mirroring ApSettingsDefaults on the backend.
    companyAutoAssignEnabled: settings.companyAutoAssignEnabled ?? true,
  };
}

/** Payables tab: the client's AP configuration (GET + PATCH /settings/ap), applied to every organisation, saved in one PATCH. */
export function PayablesSettingsSection() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  const [settings, setSettings] = useState<ApSettingsResponse | null>(null);
  const [op, setOp] = useState<OperationalState | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Errors about the payment link mode or template, shown inside the Payment links card.
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/settings/ap", {});
      if (cancelled) return;
      if (apiError || !data) {
        setError("Failed to load payables settings.");
      } else {
        setSettings(data);
        setOp(toOperationalState(data));
        setForm(toFormState(data));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const savedOp = useMemo(() => (settings ? toOperationalState(settings) : null), [settings]);
  const savedForm = useMemo(() => (settings ? toFormState(settings) : null), [settings]);

  const opDirty = useMemo(() => {
    if (!op || !savedOp)
      return {
        apEmailsEnabled: false,
        billForwardingSlug: false,
        billReviewTtlDays: false,
        gmailConnectorEnabled: false,
        gmailAllowedRedirectHosts: false,
        payByBankEnabled: false,
        paymentAllowedRedirectHosts: false,
        paymentLinkMode: false,
        defaultBillPaymentLinkUrl: false,
        poAutoMatchEnabled: false,
        dnAutoMatchEnabled: false,
        duplicateBillDetectionEnabled: false,
        companyAutoAssignEnabled: false,
      };
    return {
      apEmailsEnabled: op.apEmailsEnabled !== savedOp.apEmailsEnabled,
      billForwardingSlug: op.billForwardingSlug !== savedOp.billForwardingSlug,
      billReviewTtlDays: op.billReviewTtlDays !== savedOp.billReviewTtlDays,
      gmailConnectorEnabled: op.gmailConnectorEnabled !== savedOp.gmailConnectorEnabled,
      gmailAllowedRedirectHosts: op.gmailAllowedRedirectHosts !== savedOp.gmailAllowedRedirectHosts,
      payByBankEnabled: op.payByBankEnabled !== savedOp.payByBankEnabled,
      paymentAllowedRedirectHosts: op.paymentAllowedRedirectHosts !== savedOp.paymentAllowedRedirectHosts,
      paymentLinkMode: op.paymentLinkMode !== savedOp.paymentLinkMode,
      // Trimmed like the AR template: the server stores it trimmed, so trailing spaces aren't a change.
      defaultBillPaymentLinkUrl: op.defaultBillPaymentLinkUrl.trim() !== savedOp.defaultBillPaymentLinkUrl,
      poAutoMatchEnabled: op.poAutoMatchEnabled !== savedOp.poAutoMatchEnabled,
      dnAutoMatchEnabled: op.dnAutoMatchEnabled !== savedOp.dnAutoMatchEnabled,
      duplicateBillDetectionEnabled:
        op.duplicateBillDetectionEnabled !== savedOp.duplicateBillDetectionEnabled,
      companyAutoAssignEnabled: op.companyAutoAssignEnabled !== savedOp.companyAutoAssignEnabled,
    };
  }, [op, savedOp]);

  const knobsDirty = !!savedForm && !!form && ALL_FIELDS.some((f) => form[f.key] !== savedForm[f.key]);
  const anyDirty = Object.values(opDirty).some(Boolean) || knobsDirty;
  const isFieldDirty = (key: NumericFieldKey) => !!savedForm && !!form && form[key] !== savedForm[key];

  function setKnob(key: NumericFieldKey, value: string) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function reset() {
    if (savedOp) setOp(savedOp);
    if (savedForm) setForm(savedForm);
    setError(null);
    setLinkError(null);
  }

  /** Inserts a token at the cursor when the template input has focus, otherwise appends it. */
  function insertToken(token: string) {
    if (!op) return;
    const el = linkInputRef.current;
    const value = op.defaultBillPaymentLinkUrl;
    const focused = el !== null && document.activeElement === el;
    const start = focused ? (el.selectionStart ?? value.length) : value.length;
    const end = focused ? (el.selectionEnd ?? value.length) : value.length;
    setOp({ ...op, defaultBillPaymentLinkUrl: value.slice(0, start) + token + value.slice(end) });
    if (focused) {
      const caret = start + token.length;
      requestAnimationFrame(() => el.setSelectionRange(caret, caret));
    }
  }

  async function save() {
    if (!op || !form || !anyDirty || saving) return;

    const body: PatchApSettingsRequest = {};

    // Operational config — only the dirty fields.
    if (opDirty.apEmailsEnabled) body.apEmailsEnabled = op.apEmailsEnabled;
    if (opDirty.billForwardingSlug) {
      const slug = op.billForwardingSlug.trim().toLowerCase();
      if (slug === "") {
        setError("Forwarding subdomain can't be empty — leave it unchanged to keep the client-name default.");
        return;
      }
      if (!isValidSlug(slug)) {
        setError("Forwarding subdomain must be 1–63 lowercase letters, digits or hyphens, with no leading or trailing hyphen (and no dots).");
        return;
      }
      body.billForwardingSlug = slug;
    }
    if (opDirty.billReviewTtlDays) {
      const parsed = Number(op.billReviewTtlDays);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        setError("Bill review TTL must be a positive whole number of days.");
        return;
      }
      body.billReviewTimeAfterDueDate = parsed;
    }
    if (opDirty.gmailConnectorEnabled) body.gmailConnectorEnabled = op.gmailConnectorEnabled;
    if (opDirty.gmailAllowedRedirectHosts)
      body.gmailAllowedRedirectHosts = parseHosts(op.gmailAllowedRedirectHosts);
    if (opDirty.payByBankEnabled) body.payByBankEnabled = op.payByBankEnabled;
    if (opDirty.paymentAllowedRedirectHosts)
      body.paymentAllowedRedirectHosts = parseHosts(op.paymentAllowedRedirectHosts);
    // Mirrors the server rule so the user gets the message in the card without a round trip. Only when one of
    // the two changed: the server re-checks just then, so an already-saved combination never blocks other edits.
    if (
      op.paymentLinkMode === "Dolfin" &&
      !op.payByBankEnabled &&
      (opDirty.paymentLinkMode || opDirty.payByBankEnabled)
    ) {
      setLinkError("Dolfin payment links need pay-by-bank. Turn pay-by-bank back on, or choose another payment link.");
      return;
    }
    if (opDirty.paymentLinkMode) body.paymentLinkMode = op.paymentLinkMode;
    // The template is saved in any mode (only Your own payment link uses it); "" clears it.
    if (opDirty.defaultBillPaymentLinkUrl) body.defaultBillPaymentLinkUrl = op.defaultBillPaymentLinkUrl.trim();
    if (opDirty.poAutoMatchEnabled) body.poAutoMatchEnabled = op.poAutoMatchEnabled;
    if (opDirty.dnAutoMatchEnabled) body.dnAutoMatchEnabled = op.dnAutoMatchEnabled;
    if (opDirty.duplicateBillDetectionEnabled)
      body.duplicateBillDetectionEnabled = op.duplicateBillDetectionEnabled;
    if (opDirty.companyAutoAssignEnabled) body.companyAutoAssignEnabled = op.companyAutoAssignEnabled;

    // Matching knobs — send the full set when any knob changed (validated).
    if (knobsDirty) {
      for (const field of ALL_FIELDS) {
        const raw = form[field.key];
        if (raw.trim() === "") {
          toast.error(`${field.label} can't be empty`);
          return;
        }
        const parsed = field.integer ? parseInt(raw, 10) : parseFloat(raw);
        if (Number.isNaN(parsed) || parsed < field.min || (field.max != null && parsed > field.max)) {
          toast.error(`${field.label} is out of range`);
          return;
        }
        (body as Record<string, number>)[field.key] = parsed;
      }
      if ((body.supplierMatchNameWeight ?? 0) === 0 && (body.supplierMatchAddressWeight ?? 0) === 0) {
        toast.error("Name and address weight can't both be zero");
        return;
      }
      // Mirrors the server rule: auto-accepting a match the engine wouldn't even suggest is nonsensical.
      if (Number(body.matchAutoAcceptThreshold) < Number(body.matchSimilarityThreshold)) {
        toast.error("Auto-accept score can't be below the line match similarity threshold");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setLinkError(null);
    const { data, error: apiError } = await api.PATCH("/v1/settings/ap", { body });
    setSaving(false);
    if (apiError || !data) {
      const problem = apiError as { code?: string; detail?: string } | undefined;
      const detail = problem?.detail ?? "Failed to save payables settings.";
      if (isPaymentLinkProblem(problem, body)) setLinkError(detail);
      else setError(detail);
      return;
    }
    setSettings(data);
    setOp(toOperationalState(data));
    setForm(toFormState(data));
    toast.success("Payables settings saved");
  }

  function renderKnob(field: FieldSpec, extraDisabled = false) {
    return (
      <div key={field.key} className="space-y-1.5">
        <Label htmlFor={field.key} className="text-sm">
          {field.label}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id={field.key}
            type="number"
            inputMode="decimal"
            min={field.min}
            max={field.max}
            step={field.step}
            disabled={!isAdmin || loading || extraDisabled}
            value={form?.[field.key] ?? ""}
            onChange={(e) => setKnob(field.key, e.target.value)}
            className={cn("max-w-[9rem]", isFieldDirty(field.key) && dirtyRing)}
          />
          {field.suffix && <span className="text-sm text-muted-foreground">{field.suffix}</span>}
        </div>
        <p className="text-xs text-muted-foreground">{field.help}</p>
      </div>
    );
  }

  useSaveBarSection({ dirty: anyDirty, saving, save, discard: reset });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading payables settings…
        </CardContent>
      </Card>
    );
  }

  if (!op || !form) {
    return (
      <Card>
        <CardContent className="py-12 text-sm text-destructive">
          {error ?? "Unable to load payables settings."}
        </CardContent>
      </Card>
    );
  }

  const linkTokens = settings?.supportedBillPaymentLinkTokens ?? [];

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        Accounts-payable configuration and how bills are matched to purchase orders, delivery notes and
        suppliers. These apply to every organisation under your client and take effect immediately.
        {!isAdmin && " You need an Owner or Admin role to edit these."}
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Notification emails</CardTitle>
          <CardDescription>
            Whether Dolfin emails your Organisations' users about bills awaiting approval and their
            outcome.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ap-emails-enabled">Send payables emails</Label>
              <p className="text-xs text-muted-foreground">
                When on, approvers are emailed a link when a bill needs their approval, and whoever
                submitted it is emailed when it's approved or rejected. When off, Dolfin sends none of
                these — bills still move through approval exactly as before and everything stays visible
                in the app, so your Organisations notify their users themselves.
              </p>
            </div>
            <Switch
              id="ap-emails-enabled"
              checked={op.apEmailsEnabled}
              disabled={!isAdmin || saving}
              onCheckedChange={(checked) => setOp({ ...op, apEmailsEnabled: checked })}
              className={cn(opDirty.apEmailsEnabled && dirtyRing)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bill forwarding address</CardTitle>
          <CardDescription>
            The subdomain shown in your organisations' bill-forwarding email addresses.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="bill-forwarding-slug">Forwarding subdomain</Label>
            <Input
              id="bill-forwarding-slug"
              value={op.billForwardingSlug}
              placeholder="defaults to a slug of your client name"
              disabled={!isAdmin || saving}
              onChange={(e) => setOp({ ...op, billForwardingSlug: e.target.value })}
              className={cn("max-w-[22rem] font-mono", opDirty.billForwardingSlug && dirtyRing)}
            />
            <p className="text-xs text-muted-foreground">
              Addresses read{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                bills.&lt;token&gt;@{op.billForwardingSlug.trim().toLowerCase() || "your-client"}
                .accountspayable.co
              </code>
              . A single DNS label — lowercase letters, digits or hyphens, no dots. It's cosmetic:
              bills route by each organisation's private token, not this label. Leave unchanged to keep
              the default (a slug of your client name).
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bill approval</CardTitle>
          <CardDescription>How long suppliers can review a bill via its public link.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="bill-review-ttl">Bill review window (days after due date)</Label>
            <Input
              id="bill-review-ttl"
              type="number"
              min={1}
              step={1}
              value={op.billReviewTtlDays}
              disabled={!isAdmin || saving}
              onChange={(e) => setOp({ ...op, billReviewTtlDays: e.target.value })}
              className={cn("max-w-[12rem]", opDirty.billReviewTtlDays && dirtyRing)}
            />
            <p className="text-xs text-muted-foreground">
              Suppliers can review a bill via its public link up to this many days past its due date.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Duplicate bills</CardTitle>
          <CardDescription>
            Catches the same supplier invoice being received twice, so it can't be paid twice.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="duplicate-detection-enabled">Detect duplicate bills</Label>
              <p className="text-xs text-muted-foreground">
                When a bill is received with the same supplier and invoice number as an earlier one, it's
                flagged and can't be approved until someone confirms it's a separate invoice. Nothing is
                ever voided automatically. Turn this off only for testing — for example when repeatedly
                re-uploading the same document.
              </p>
            </div>
            <Switch
              id="duplicate-detection-enabled"
              checked={op.duplicateBillDetectionEnabled}
              disabled={!isAdmin || saving}
              onCheckedChange={(checked) => setOp({ ...op, duplicateBillDetectionEnabled: checked })}
              className={cn(opDirty.duplicateBillDetectionEnabled && dirtyRing)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gmail connector</CardTitle>
          <CardDescription>
            Lets Organisations grant your portal access to their Gmail inbox to ingest bill attachments via OAuth.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="gmail-enabled">Enable Gmail connector</Label>
              <p className="text-xs text-muted-foreground">
                When off, the OAuth initiation endpoint returns 403 for this client's organisations.
              </p>
            </div>
            <Switch
              id="gmail-enabled"
              checked={op.gmailConnectorEnabled}
              disabled={!isAdmin || saving}
              onCheckedChange={(checked) => setOp({ ...op, gmailConnectorEnabled: checked })}
              className={cn(opDirty.gmailConnectorEnabled && dirtyRing)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="gmail-hosts">Allowed OAuth redirect hosts</Label>
            <Textarea
              id="gmail-hosts"
              placeholder="portal.example.com&#10;app.example.com"
              value={op.gmailAllowedRedirectHosts}
              disabled={!isAdmin || saving}
              rows={4}
              onChange={(e) => setOp({ ...op, gmailAllowedRedirectHosts: e.target.value })}
              className={cn("font-mono text-xs", opDirty.gmailAllowedRedirectHosts && dirtyRing)}
            />
            <p className="text-xs text-muted-foreground">
              One host per line (commas also accepted). Bare hostnames only — no scheme, port, or path.
              Connection requests with redirect URIs outside this list are rejected.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bill payments</CardTitle>
          <CardDescription>How your Organisations settle their approved bills.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="pay-by-bank-enabled">Enable pay-by-bank (open banking)</Label>
              <p className="text-xs text-muted-foreground">
                Lets Organisations pay approved bills straight from their bank account via Yapily.
                Manual confirm-paid (cash, bank transfer, cheque, card) is always available regardless.
              </p>
            </div>
            <Switch
              id="pay-by-bank-enabled"
              checked={op.payByBankEnabled}
              disabled={!isAdmin || saving}
              onCheckedChange={(checked) => setOp({ ...op, payByBankEnabled: checked })}
              className={cn(opDirty.payByBankEnabled && dirtyRing)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="payment-hosts">Allowed payment redirect hosts</Label>
            <Textarea
              id="payment-hosts"
              placeholder="portal.example.com&#10;app.example.com"
              value={op.paymentAllowedRedirectHosts}
              disabled={!isAdmin || saving}
              rows={4}
              onChange={(e) => setOp({ ...op, paymentAllowedRedirectHosts: e.target.value })}
              className={cn("font-mono text-xs", opDirty.paymentAllowedRedirectHosts && dirtyRing)}
            />
            <p className="text-xs text-muted-foreground">
              One host per line (commas also accepted). Bare hostnames only — no scheme, port, or path.
              After a pay-by-bank authorisation the payer is redirected to the bill's returnUrl; requests
              whose host is outside this list are rejected.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            Payment links
            <InfoHint label="Payment links">
              The link appears on each approved bill, in bill webhooks and in the ready-to-pay email sent to
              the Organisation's admins.
            </InfoHint>
          </CardTitle>
          <CardDescription>Which payment link your Organisations' approved bills carry.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Payment link">
            {PAYMENT_LINK_OPTIONS.map((opt) => {
              const selected = op.paymentLinkMode === opt.value;
              // Judged on the form, not the saved settings, so pay-by-bank and Dolfin links can be switched on in
              // one save. Never disables the selected option: turning pay-by-bank off under it shows a warning instead.
              const needsPayByBank = opt.value === "Dolfin" && !op.payByBankEnabled && !selected;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!isAdmin || saving || needsPayByBank}
                  onClick={() => setOp({ ...op, paymentLinkMode: opt.value })}
                  className={cn(
                    "flex flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition disabled:cursor-not-allowed",
                    selected
                      ? "border-primary/60 bg-primary/10 ring-2 ring-primary/40 dark:bg-primary/15"
                      : "border-border hover:border-foreground/30 disabled:hover:border-border",
                    needsPayByBank && "opacity-60",
                    selected && opDirty.paymentLinkMode && dirtyRing,
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <span
                      aria-hidden
                      className={cn(
                        "size-3.5 shrink-0 rounded-full border",
                        selected ? "border-4 border-primary" : "border-muted-foreground/50",
                      )}
                    />
                    {opt.label}
                  </span>
                  <span className="pl-5.5 text-xs leading-snug text-muted-foreground">
                    {needsPayByBank ? "Turn on pay-by-bank above to use Dolfin payment links." : opt.description}
                  </span>
                </button>
              );
            })}
          </div>

          {op.paymentLinkMode === "Dolfin" && !op.payByBankEnabled && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Pay-by-bank is off, so Dolfin payment links can't be used. Turn it back on above, or choose another
              payment link.
            </p>
          )}

          {op.paymentLinkMode === "BringYourOwn" && (
            <div className="flex flex-col gap-2 border-t pt-5">
              <Label htmlFor="bill-payment-link-url">Your payment link</Label>
              <p className="text-xs text-muted-foreground">
                The URL each bill links to. Use the tokens below and Dolfin fills them in per bill, so your
                system knows which bill is being paid.
              </p>
              <Input
                id="bill-payment-link-url"
                ref={linkInputRef}
                type="url"
                inputMode="url"
                placeholder="https://pay.yourcompany.com/bills/{billId}"
                value={op.defaultBillPaymentLinkUrl}
                disabled={!isAdmin || saving}
                aria-invalid={linkError ? true : undefined}
                onChange={(e) => setOp({ ...op, defaultBillPaymentLinkUrl: e.target.value })}
                className={cn("font-mono text-xs md:text-xs", opDirty.defaultBillPaymentLinkUrl && dirtyRing)}
              />
              {linkTokens.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-xs text-muted-foreground">Insert a token:</span>
                  {linkTokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      disabled={!isAdmin || saving}
                      // Keeps focus (and the cursor position) in the input so the token lands where the cursor is.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertToken(token)}
                      aria-label={`Insert ${token}`}
                      className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground transition hover:bg-accent-blue/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {token}
                    </button>
                  ))}
                </div>
              )}
              {op.defaultBillPaymentLinkUrl.trim() ? (
                <div className="flex flex-col gap-1 rounded-md bg-muted/60 px-3 py-2 dark:bg-muted/40">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Example
                    <InfoHint label="Example link">
                      Uses sample ids. Each bill fills in its own, URL-encoded.
                    </InfoHint>
                  </span>
                  <code className="break-all font-mono text-xs text-foreground">
                    {expandSampleLink(op.defaultBillPaymentLinkUrl.trim())}
                  </code>
                </div>
              ) : (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Bills carry no payment link until you set one.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Must be an absolute <code className="font-mono">https</code> URL of up to 2048 characters.
              </p>
            </div>
          )}

          {linkError && <p className="text-sm text-destructive">{linkError}</p>}
        </CardContent>
      </Card>

      {SHOW_MATCHING_SETTINGS && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <SlidersHorizontal className="size-4" />
                Line matching
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">{LINE_FIELDS.map((f) => renderKnob(f))}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Sparkles className="size-4" />
                Automatic matching
              </CardTitle>
              <CardDescription>
                Controls whether bill lines are matched automatically against purchase orders and delivery
                notes. Each route can be switched off independently — so you can run delivery-note matching
                on its own, with purchase orders staying out of the way.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="po-auto-match-enabled">Match against purchase orders</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, nothing is matched to a purchase order without a reviewer — including an
                    identical SKU. Suggestions are still offered and can be linked by hand.
                  </p>
                </div>
                <Switch
                  id="po-auto-match-enabled"
                  checked={op.poAutoMatchEnabled}
                  disabled={!isAdmin || saving}
                  onCheckedChange={(checked) => setOp({ ...op, poAutoMatchEnabled: checked })}
                  className={cn(opDirty.poAutoMatchEnabled && dirtyRing)}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="dn-auto-match-enabled">Match against delivery notes</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, nothing is matched to a delivery note without a reviewer — including an
                    identical SKU. Suggestions are still offered and can be linked by hand.
                  </p>
                </div>
                <Switch
                  id="dn-auto-match-enabled"
                  checked={op.dnAutoMatchEnabled}
                  disabled={!isAdmin || saving}
                  onCheckedChange={(checked) => setOp({ ...op, dnAutoMatchEnabled: checked })}
                  className={cn(opDirty.dnAutoMatchEnabled && dirtyRing)}
                />
              </div>
              {/* Qualifies only the delivery-note route, so it's disabled when that route is off. */}
              {/* The score bars apply to fuzzy (non-SKU) candidates on whichever routes are still on. */}
              {AUTO_ACCEPT_FIELDS.map((f) =>
                renderKnob(f, !op.poAutoMatchEnabled && !op.dnAutoMatchEnabled),
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Building className="size-4" />
                Company bill routing
              </CardTitle>
              <CardDescription>
                Applies only to bills received against a company rather than a specific organisation, either
                uploaded there or forwarded to its bill address. The delivery address printed on the bill is
                matched against the addresses of that company's organisations. Bills sent straight to an
                organisation are unaffected.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="company-auto-assign-enabled">Assign to an organisation automatically</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, every bill received against a company waits in that company's inbox for someone
                    to assign it. Bills delivered to a company's head office, and bills naming several sites,
                    always wait for a person regardless of this setting.
                  </p>
                </div>
                <Switch
                  id="company-auto-assign-enabled"
                  checked={op.companyAutoAssignEnabled}
                  disabled={!isAdmin || saving}
                  onCheckedChange={(checked) => setOp({ ...op, companyAutoAssignEnabled: checked })}
                  className={cn(opDirty.companyAutoAssignEnabled && dirtyRing)}
                />
              </div>
              {COMPANY_ROUTING_FIELDS.map((f) => renderKnob(f, !op.companyAutoAssignEnabled))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <SlidersHorizontal className="size-4" />
                Variance tolerances
              </CardTitle>
              <CardDescription>
                How much a matched line's price and quantity may differ from the purchase order or delivery note
                before it needs a person. Each tolerance does two jobs at once: within it a difference is treated as
                clean — it doesn't count against the match confidence and an automatically matched line is settled
                with no reviewer; beyond it, the difference lowers confidence and the line is held for someone to
                accept. These apply whether or not automatic matching is on, because they also shape suggestion
                confidence.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">{VARIANCE_FIELDS.map((f) => renderKnob(f))}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Sparkles className="size-4" />
                Learning from manual matches
              </CardTitle>
              <CardDescription>
                Confirming one match tells us two things: that this line probably continues on other deliveries of
                the same item, and that the rest of the bill probably came off the document you just used. Both
                only re-rank suggestions for the bill in front of you — nothing is remembered between bills, and
                neither will ever match a line for you automatically. Each is a percentage uplift applied to the
                finished match score and capped at 100%, so a near-certain suggestion gains little and a
                middling one gains most.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">{LINK_AFFINITY_FIELDS.map((f) => renderKnob(f))}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <SlidersHorizontal className="size-4" />
                Supplier matching
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">{SUPPLIER_FIELDS.map((f) => renderKnob(f))}</CardContent>
          </Card>
        </>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

    </div>
  );
}
